import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { useDebounce, useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { SignatureServices } from "../../../shared/services/api/signature/SignatureServices";
import { EnvelopeMenu, EnvelopeMenuAction } from "../components/EnvelopeMenu";
import EnvelopeEditModal from "../modals/EnvelopeEditModal";
import type { IEnvelopeData } from "../types/types";
import type { EnvelopeTab as TableEnvelopeTab } from "./tableEnvelopes";
import { TableEnvelope } from "./tableEnvelopes";
import { SignatureToolbar } from "./ToolbarSignature";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";

type ModalEnvelopeTab = TableEnvelopeTab;

function getMonthDefaultRange() {
	const today = new Date();
	const ini = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
	const fim = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
	return { ini, fim };
}

export const SignaturePresentation = () => {
	const { debounce } = useDebounce();
	const toastHandler = useToast();

	// Estado para os valores dos campos do formulário de busca
	const [textSearch, setTextSearch] = useState("");
	const [dataIni, setDataIni] = useState(getMonthDefaultRange().ini);
	const [dataFim, setDataFim] = useState(getMonthDefaultRange().fim);
	const [status, setStatus] = useState("todos");

	// Estado que realmente aciona a busca (aplicando os filtros)
	const [appliedFilters, setAppliedFilters] = useState({
		text: "",
		dataIni: getMonthDefaultRange().ini,
		dataFim: getMonthDefaultRange().fim,
		status: "todos",
	});

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [data, setData] = useState<IEnvelopeData[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	// Estado consolidado para o modal de edição
	const [modalState, setModalState] = useState({
		open: false,
		row: undefined as IEnvelopeData | undefined,
		isNew: false,
		initialTab: "dados" as ModalEnvelopeTab,
	});

	// Estado para o menu de ações da tabela
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [selectedMenuRow, setSelectedMenuRow] = useState<IEnvelopeData | null>(null);

	const normalizeStatus = (raw: string) => (raw === "todos" ? "" : raw);

	useEffect(() => {
		setIsLoading(true);
		const doFetch = () => {
			SignatureServices.getEnvelope(
				page + 1,
				rowsPerPage,
				appliedFilters.text,
				appliedFilters.dataIni,
				appliedFilters.dataFim,
				normalizeStatus(appliedFilters.status)
			)
				.then((response) => {
					if (response instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Erro" content="Erro ao buscar envelopes" />,
						});
						return;
					}
					setData(response.data as IEnvelopeData[]);
					setTotalCount(response.totalCount ?? 0);
				})
				.finally(() => setIsLoading(false));
		};

		debounce(doFetch);
	}, [page, rowsPerPage, refreshTrigger, appliedFilters]);

	const handleSearch = () => {
		setAppliedFilters({
			text: textSearch ?? "",
			dataIni: dataIni,
			dataFim: dataFim,
			status: status,
		});
		setPage(0);
	};

	const handleChangePage = (newPage: number) => setPage(newPage);
	const handleChangeRowsPerPage = (newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setPage(0);
	};

	const refreshList = () => setRefreshTrigger((prev) => prev + 1);

	const handleOpenCreate = () => {
		setModalState({ open: true, row: undefined, isNew: true, initialTab: "dados" });
	};

	const handleOpenEnvelope = (id: string, tab?: TableEnvelopeTab) => {
		const row = data.find((r) => r.id === id);
		if (!row) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content="Envelope não encontrado na lista atual." />,
			});
			return;
		}
		setModalState({ open: true, row, isNew: false, initialTab: tab ?? "dados" });
	};

	const handleCloseEdit = () => {
		setModalState({ open: false, row: undefined, isNew: false, initialTab: "dados" });
	};

	const handleSuccessRefresh = () => {
		refreshList();
	};

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: IEnvelopeData) => {
		setMenuAnchorEl(event.currentTarget);
		setSelectedMenuRow(row);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
		setSelectedMenuRow(null);
	};

	// Funções de ação centralizadas
	const handleMenuAction = async (action: EnvelopeMenuAction, envelopeId: string) => {
		setIsLoading(true);
		let serviceCall;
		let successMessage;
		let errorMessage;

		if (action === "sendToSign") {
			await SignatureServices.sendToSign(envelopeId);
			successMessage = "Envelope enviado para assinatura com sucesso!";
			errorMessage = "Erro ao enviar envelope para assinatura.";
		} else if (action === "cancel") {
			serviceCall = SignatureServices.cancelEnvelope(envelopeId);
			successMessage = "Envelope cancelado com sucesso.";
			errorMessage = "Erro ao cancelar envelope.";
		} else {
			// AQUI ESTÁ O ERRO: o 'action' pode ser 'dados', 'documentos' ou 'signatarios', não 'edit'
			handleOpenEnvelope(envelopeId, action as TableEnvelopeTab);
			handleCloseMenu();
			setIsLoading(false);
			return;
		}

		try {
			if (serviceCall) {
				await serviceCall;
			}
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content={successMessage} />,
			});
			refreshList();
		} catch (error: any) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content={errorMessage || error.message || "Erro desconhecido."} />,
			});
		} finally {
			setIsLoading(false);
			handleCloseMenu();
		}
	};

	const handleMenuSelect = (action: EnvelopeMenuAction) => {
		if (selectedMenuRow) {
			handleMenuAction(action, selectedMenuRow.id);
		}
	};

	return (
		<LayoutBaseDePagina titulo="Assinaturas" subtitulo="Gestão de Assinaturas de Documentos e Envelopes" barraDeFerramentas={<></>}>
			<Box sx={{ pt: 1 }}>
				<SignatureToolbar
					showInputSearch
					showDateRange
					showStatusSelect
					showNewEnvelopeButton={checkByProfileGuard(PermissionRolesEnum.SinisterMenu, PermissionActionsEnum.View)}
					textSearch={textSearch}
					onChangeTextSearch={setTextSearch}
					onClearTextSearch={() => setTextSearch("")}
					onChangeDateStart={setDataIni}
					onChangeDateEnd={setDataFim}
					onChangeStatus={setStatus}
					onClickRefresh={handleSearch}
					onClickNewEnvelope={handleOpenCreate}
				/>
				<LoadingComp isopen={isLoading} />
				<TableEnvelope
					rows={data}
					totalCount={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					onOpenEnvelope={handleOpenEnvelope}
					onOpenMenu={handleOpenMenu}
				/>
			</Box>
			{modalState.open && (
				<EnvelopeEditModal
					open={modalState.open}
					onClose={handleCloseEdit}
					row={modalState.row}
					isNew={modalState.isNew}
					initialTab={modalState.initialTab}
					onSuccessRefresh={handleSuccessRefresh}
				/>
			)}
			<EnvelopeMenu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleCloseMenu}
				selectedRow={selectedMenuRow}
				onSelect={handleMenuSelect}
			/>
		</LayoutBaseDePagina>
	);
};
